// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerFilter.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action, toJS } from 'mobx';
import FilterDetails from './Izenda.FilterDetails';
import uiConstants from 'logic/data/Izenda.UIConstants';
import constants, { FILTER_URL } from 'logic/data/Izenda.ReportDesignerConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import filterConstants, {
  EXCLUDE_FILTER_DATA_TYPE,
  OPERATOR_NAMES
} from 'logic/data/Izenda.FilterDetailsConfigConstants';
import FilterDetailsConfig from './Izenda.FilterDetailsConfig';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import {
  filter,
  get,
  isEmpty,
  isEqual,
  map,
  clone,
  find,
  getOr,
  findIndex,
  isNil,
  flow,
  invokeArgs,
  forEach,
  assign,
  isNumber,
  size,
  head,
  some,
  reduce,
  set,
  without,
  includes,
  split,
  trim,
  difference,
  orderBy
} from 'lodash/fp';

@tracker
export default class ReportDesignerFilter {
  @observable
  id = null;
  @observable
  logic = '';
  @observable
  visible = false;
  @observable
  filterFields = [];
  @observable
  configField = new FilterDetailsConfig({}, this.markUpdateResult.bind(this));
  @observable
  configFieldList = [];
  @observable
  needUpdateResult = false;
  /* common data for filter section */
  @observable
  commonData = { querySource: [] };
  // unsaved value
  @observable
  newestFilter = null;
  @observable
  hasCascadingChanged: boolean = false;

  // Should not show the cascading changed message when reload
  showCascadingChangedMessage: boolean = true;

  @computed
  get visibleConfigFields(): Array<any> {
    return filter(item => get('fieldDetails.visible', item), this.configFieldList);
  }

  /* Get empty and required param filters */
  @computed
  get emptyRequiredParamFilters(): Array<FilterDetails> {
    return filter(field => field.required, this.emptyParamFilters);
  }

  /* Find filter made from input params with empty value */
  @computed
  get emptyParamFilters(): Array<FilterDetails> {
    return filter(field => {
      const booleanName = filterConstants.OPERATOR_GROUP_KEYS.BOOLEAN;
      const { isParameter, dataType, value, operatorId } = field;
      return isParameter && ((dataType !== booleanName && isEmpty(value)) || (dataType === booleanName && !operatorId));
    }, this.displayFilterFields);
  }
  /* Find normal filter marked as "Required" and "Not Visible" with empty value */
  @computed
  get emptyNotVisibleRequiredFilter(): Array<FilterDetails> {
    return filter(field => this.isEmptyRequiredFilter(field) && !field.visible, this.displayFilterFields);
  }
  /* Find normal filter marked as "Required" and "Visible" with empty value */
  @computed
  get emptyVisibleRequiredFilter(): Array<FilterDetails> {
    return filter(field => this.isEmptyRequiredFilter(field) && field.visible, this.displayFilterFields);
  }

  @computed
  get paramFilterFields(): Array<FilterDetails> {
    return filter({ isParameter: true }, this.displayFilterFields);
  }

  @computed
  get displayFilterFields(): Array<FilterDetails> {
    return filter(field => {
      return (
        field.dataType != EXCLUDE_FILTER_DATA_TYPE.REF_CURSOR &&
        field.dataType != EXCLUDE_FILTER_DATA_TYPE.SYS_REFCURSOR &&
        field.state !== constants.OBJECT_STATE.DELETE
      );
    }, this.filterFields);
  }

  @computed
  get availableFilterFields(): Array<FilterDetails> {
    return filter(({ state }) => state !== constants.OBJECT_STATE.DELETE, this.filterFields);
  }

  @computed
  get displayConfigFields(): Array<any> {
    return filter(configField => {
      const field = configField.fieldDetails;
      return (
        !isEqual(field.dataType, EXCLUDE_FILTER_DATA_TYPE.REF_CURSOR) &&
        !isEqual(field.dataType, EXCLUDE_FILTER_DATA_TYPE.SYS_REFCURSOR) &&
        field.state !== constants.OBJECT_STATE.DELETE &&
        field.visible
      );
    }, this.configFieldList);
  }

  constructor(initData: any, isReload: boolean = false) {
    this.showCascadingChangedMessage = !isReload;
    initData && this.populateData(initData);
    this.cleanDirty();
  }

  /**
   * Reset config field to default
   */
  @action
  resetConfigField() {
    this.configField.setFieldDetails(null);
  }

  /**
   * Set field which is set as configuration mode (user can edit its properties)
   * @param {FilterDetails} newField
   */
  @action
  setConfigModeToField(newField: any) {
    if (!newField) {
      return this.resetConfigField();
    }

    if (isEqual(this.configField.fieldDetails, newField)) {
      // already in config mode, do nothing
      return;
    }

    const currentConfigField = this.configField;

    if (currentConfigField.fieldDetails && currentConfigField.fieldDetails.querySourceFieldId) {
      // update filter value to field details
      currentConfigField.updateFilterValueToField();
      // reset cache filter data
      currentConfigField.resetOperatorSourceDisplay();
    }

    // set newField in config mode
    this.configField.setFieldDetails(newField);
  }

  /**
   * Update configField list
   * @param {Object} subscriptionFilterFields
   * @param {Object} filterSourceData
   * @param {boolean} viewMode
   */
  @action
  updateConfigFieldList(subscriptionFilterFields: any, filterSourceData: any, viewMode: boolean) {
    filterSourceData = filterSourceData || [];

    const filterFields = this.filterFields;
    this.configFieldList = map(item => {
      const filterDetailsConfig = new FilterDetailsConfig(item, this.markUpdateResult);
      filterDetailsConfig.commonData = this.configField.commonData;

      if (!viewMode) {
        filterDetailsConfig.operatorSourceInfo = clone(this.configField.operatorSourceInfo);
        if (subscriptionFilterFields && subscriptionFilterFields.length > 0) {
          const fieldDetails = filterDetailsConfig.fieldDetails;
          const subscriptionFilterField = find({ filterFieldId: fieldDetails.id }, subscriptionFilterFields);
          if (!isNil(subscriptionFilterField)) {
            const { value, operatorId, operatorSetting, position } = subscriptionFilterField;
            fieldDetails.value = value;
            fieldDetails.operatorId = operatorId;
            filterDetailsConfig.selectedOperatorId = operatorId;
            fieldDetails.operatorSetting = operatorSetting;
            fieldDetails.position = position;
          }
        }
      }

      return filterDetailsConfig;
    }, filterFields);
  }

  /**
   * populate filter data
   * @param {Object} filterSourceData
   */
  @action
  populatedFilterSourceData(filterSourceData: any) {
    const filterFields = this.filterFields;
    filterSourceData = filterSourceData || [];

    this.configFieldList = map(item => {
      const filterDetailsConfig = new FilterDetailsConfig(item, this.markUpdateResult.bind(this));
      filterDetailsConfig.commonData = this.configField.commonData;

      const sourceType = filterDetailsConfig.filterInfo.sourceType;
      const filterId = filterDetailsConfig.fieldDetails.id;
      const filterData =
        find(
          {
            filterFieldId: filterId
          },
          filterSourceData
        ) || {};
      const operatorSourceValue = getOr([], 'data', filterData);
      filterDetailsConfig.populateOperatorSourceValue(sourceType, operatorSourceValue);

      return filterDetailsConfig;
    }, filterFields);
  }

  /**
   * Reset config mode (field for config will be set as null)
   */
  resetConfigMode() {
    this.setConfigModeToField(null);
  }

  /**
   * Populate data of filter-report (mostly data form API)
   * @param {object} data
   */
  @action
  populateData(data: any) {
    this.id = data.id;
    this.logic = data.logic;
    this.visible = data.visible;
    this.populateFilterList(data.filterFields);
    this.updateFilterInfoForSubReport();
    this.cleanDirty();
  }

  /**
   * Populate data of list of filter-fields (mostly data form API)
   * @param {type} filterList
   */
  @action
  populateFilterList(filterList: any) {
    filterList = filterList || [];

    filterList = filter(item => item.state !== constants.OBJECT_STATE.DELETE, filterList);

    this.filterFields = filterList.map(item => {
      return new FilterDetails(item, filterList, this.markUpdateResult.bind(this));
    });

    // Set filterList for filterDetail
    this.filterFields.forEach(filterField => {
      filterField.filterList = this.filterFields;
    });

    // Mark the cascading of one field has changed so the designer can show message to notify user
    this.hasCascadingChanged = this.disableCascading();

    // Set field detail for configField
    const configFieldDetails = this.configField.fieldDetails;
    if (!isEmpty(configFieldDetails)) {
      const fieldDetail = find({ sourceFieldName: configFieldDetails.sourceFieldName }, this.filterFields);
      this.configField.setFieldDetails(fieldDetail);
    }
  }

  /**
   * Set cascading to false when having filter lookup
   * @returns {boolean} The value indicate that at least one cascading value changed
   */
  @action
  disableCascading() {
    const filterFieldsToDisable = [];
    const availableItems = flow(
      filter(item => item.state !== constants.OBJECT_STATE.DELETE),
      orderBy('position', 'asc')
    )(this.filterFields);

    availableItems.forEach((item, index) => {
      const nextItem = availableItems[index + 1];

      // Apply to the field which next filter having valid filter lookup
      if (
        nextItem &&
        get('allowLookup', this.configField.getOperatorById(nextItem.operatorId)) &&
        ((nextItem.hasValidLookupKey && nextItem.useLookup) || nextItem.hasUserDefinedLookup)
      ) {
        filterFieldsToDisable.push(item);
      }
    });

    let hasCascadingChanged = false;
    filterFieldsToDisable.forEach(
      item => (hasCascadingChanged = hasCascadingChanged || item.setDisableCascading(true))
    );
    const filterFieldsToEnable = difference(availableItems, filterFieldsToDisable);
    filterFieldsToEnable.forEach(item => item.setDisableCascading(false));

    return hasCascadingChanged;
  }

  /**
   * Populate data of operator group ( to config fitler properties, mostly data form API)
   * @param {type} operatorGroup
   */
  @action
  populateOperatorGroup(operatorGroup: any) {
    this.configField.populateOperatorGroup(operatorGroup);
  }

  /**
   * Populate data of query source (mostly data form API)
   * @param {type} querySource
   */
  @action
  populateFilterQuerySource(querySource: any) {
    this.commonData.querySource = querySource;
  }

  /**
   * Remove report filter
   * @param {object} details
   */
  @action
  removeFilter(details: any) {
    const currentPos = details.position;

    const fieldIndex = findIndex(item => {
      return item.position === currentPos && item.state !== constants.OBJECT_STATE.DELETE;
    }, this.filterFields);

    if (fieldIndex === -1) return;

    if (this.filterFields[fieldIndex].id === constants.EMPTY_GUID || this.filterFields[fieldIndex].id === null) {
      this.filterFields.splice(fieldIndex, 1);
    } else {
      this.filterFields[fieldIndex].state = constants.OBJECT_STATE.DELETE;
    }
    this.reorderPosition();
    this.markDirty();
    this.markUpdateResult();
  }

  /**
   * Reorder report filter by its index
   * @param {number} startIndex
   */
  @action
  reorderPosition() {
    const length = this.filterFields.length;
    let position = 1;

    for (let i = 0; i < length; i++) {
      needToSetPosition(this.filterFields[i]) && (this.filterFields[i].position = position++);
    }

    this.disableCascading();

    function needToSetPosition(filterField) {
      return filterField.state !== constants.OBJECT_STATE.DELETE; // && (!filterField.isParameter);
    }
  }

  /**
   * Update visible value
   * @param {boolean} value
   */
  @action
  setVisible(value: boolean) {
    this.visible = value;

    this.markDirty();
    this.markUpdateResult();
  }

  /**
   * Update logic
   * @param {string} value
   */
  @action
  setLogic(value: string) {
    this.logic = value;
    this.markDirty();
    this.markUpdateResult();
  }

  /**
   * Add filter by its details
   * @param {Object} details
   * @param {number} indexToAddInView
   */
  @action
  addFilter(details: any, indexToAddInView: number) {
    details = details || {};
    details.state = constants.OBJECT_STATE.INSERT;
    details.visible = true;
    details.cascading = !details.isParameter;
    details.textColor = uiConstants.textColor;
    details.backgroundColor = uiConstants.backgroundColor;
    details.sortType = 'ASC';
    details.id = null;

    const filterItem = new FilterDetails(details, this.filterFields, this.markUpdateResult.bind(this));

    if (!isNil(indexToAddInView)) {
      // Find index of the item right before it
      // Add the item with that index + 1
      const indexToAddInList = this.findIndexInFilterListByViewIndex(indexToAddInView - 1) + 1;
      this.filterFields.splice(indexToAddInList, 0, filterItem);
    } else {
      this.filterFields.push(filterItem);
    }
    this.reorderPosition();
    this.markDirty();
    this.markUpdateResult();

    // keep a reference to latest filter which is added to filter-list
    this.newestFilter = filterItem;

    // remove filter value for all cascading filter that exists after this new filter
    if (filterItem.cascading) {
      flow(
        filter(filterSearch => {
          return filterSearch.position > filterItem.position && filterSearch.cascading;
        }),
        forEach(invokeArgs('updateByKey', ['value', '']))
      )(this.filterFields);
    }
  }

  /**
   * Load default operator.
   * @param {any} detail
   */
  @action
  loadDefaultOperator(detail: any) {
    const commonData = this.configField.commonData;
    let dataType = detail.dataType || '';
    dataType = dataType.toLowerCase();
    const defaultOperatorGroup = filterConstants.DEFAUFT_OPERATOR_GROUP[dataType];
    const groupByKey = find({ key: defaultOperatorGroup }, commonData.operatorGroup) || {};
    if (groupByKey.filterOperators) {
      const group = find({ id: groupByKey.id }, commonData.operatorGroup) || {};
      const operators = group.filterOperators || [];
      const isBlankOperator = filterConstants.BLANK_NULL_OPERATOR.indexOf(dataType) > -1;
      const defaultOperator = head(operators);
      let operator;
      if (dataType === filterConstants.DATA_TYPE.TIME.toLowerCase()) {
        operator = find({ key: filterConstants.PARAM_OPERATOR_BY_DATA_TYPE.Time[0] }, operators) || defaultOperator;
      } else {
        operator = (!isBlankOperator && find({ id: group.defaultOperatorId }, operators)) || defaultOperator;
      }
      detail.operatorId = operator.id;
      detail.operatorName = operator.name;
    }
  }

  /**
   * Add filter by id, details of field will be search on querySource
   * @param {string} fieldId
   * @param {Object} sourceDataObjectName
   * @param {Object} details
   * @param {number} itemIndex
   */
  @action
  addFilterById(fieldId: string, sourceDataObjectName: any, details: any, itemIndex: number) {
    let detailsByQuerySource = this.getFieldDetailsByIdAndSourceDataObjectName(fieldId, sourceDataObjectName) || {};

    detailsByQuerySource = toJS(detailsByQuerySource);
    detailsByQuerySource = assign(detailsByQuerySource, details);
    !detailsByQuerySource.operatorId && this.loadDefaultOperator(detailsByQuerySource);

    if (detailsByQuerySource) {
      this.addFilter(detailsByQuerySource, itemIndex);
      this.markDirty();
      this.markUpdateResult();
    }
  }

  /**
   * Add list of filter by id list
   * @param {type} fieldIds
   * @param {type} commonDetails
   */
  @action
  addFilterByIds(fieldIds: any, commonDetails: any) {
    const filterFields = this.filterFields;
    fieldIds = fieldIds || [];

    fieldIds.forEach(itemId => {
      !findFilterByQuerySourceFieldId(itemId) && this.addFilterById(itemId, null, commonDetails);
    });

    function findFilterByQuerySourceFieldId(querySourceFieldId) {
      return !!find(function(item) {
        return item.querySourceFieldId === querySourceFieldId && item.state !== ObjectStateConstants.DELETE;
      }, filterFields);
    }
  }

  /**
   * Find in query source to return field details based on fieldId and data source name (if any)
   * @param {string} fieldId
   * @param {string} sourceDataObjectName
   */
  getFieldDetailsByIdAndSourceDataObjectName(fieldId: string, sourceDataObjectName: string) {
    return find(item => {
      return (
        item.querySourceFieldId === fieldId &&
        (!sourceDataObjectName || item.sourceDataObjectName === sourceDataObjectName)
      );
    }, this.commonData.querySource);
  }

  /**
   * Build data for report saving
   */
  buildDataForSaving() {
    const filterFiedls = this.filterFields || [];

    const filterFiedlsData = filterFiedls.map(item => {
      return item.getFilterDetails();
    });

    return {
      logic: this.logic,
      visible: this.visible,
      filterFields: filterFiedlsData,
      id: this.id
    };
  }

  /**
   * move filter
   * @param {number} prevIndex
   * @param {number} nextIndex
   */
  @action
  moveTo(prevIndex: number, nextIndex: number) {
    if (!isNumber(nextIndex)) {
      return;
    }

    const availableFilters = filter(({ state }) => state !== ObjectStateConstants.DELETE, this.filterFields);
    if (nextIndex < 0 || nextIndex > size(availableFilters)) {
      return;
    }

    const firstIndexToSwap = this.findIndexInFilterListByViewIndex(prevIndex);
    const secondIndexToSwap = this.findIndexInFilterListByViewIndex(nextIndex);

    // remove the element from the array
    const movingItem = this.filterFields.splice(firstIndexToSwap, 1);
    size(movingItem) && this.filterFields.splice(secondIndexToSwap, 0, head(movingItem));

    this.reorderPosition();
    this.markDirty();
    this.markUpdateResult();
  }

  /**
   * Get dirty status.
   */
  checkDirty() {
    // Check dirty for filterFields.
    const dirtyItems = filter(item => {
      return item.checkDirty && item.checkDirty();
    }, this.filterFields);

    const configFieldDirty = some(['isDirty', true], this.configFieldList);

    return this.isDirty || configFieldDirty || (dirtyItems && dirtyItems.length > 0);
  }

  /**
   * Clean dirty status.
   */
  @action
  cleanDirty() {
    if (!this.hasCascadingChanged) {
      // clean dirty for filterFields
      forEach(item => {
        item.cleanDirty && item.cleanDirty();
      }, this.filterFields);

      forEach(item => {
        item.cleanDirty && item.cleanDirty();
      }, this.configFieldList);

      this.resetDirty();
      this.resetUpdateResult();
    }
  }

  /**
   * Check for Existing Filter
   * @param {Object} value
   * @param {Object} currentObject
   */
  checkExistingFilter(value: any, currentObject: any) {
    return String.checkDuplicate(value, without([currentObject], this.availableFilterFields), get('alias'));
  }

  /**
   * Find index of a item in the list by the index of view list
   * @param {Number} viewIndex
   */
  findIndexInFilterListByViewIndex(viewIndex: number) {
    // Position start from 1, so the item we need to find has position = index + 1 and haven't been deleted
    return findIndex(
      ({ state, position }) => state !== ObjectStateConstants.DELETE && position === viewIndex + 1,
      this.filterFields
    );
  }

  /**
   * Check if this filter use aggregated field
   * @param {Object} field
   * @param {Object} filterDetail
   */
  isFilterUseAggregatedField(field: any, filterDetail: any) {
    return (
      filterDetail.reportFieldAlias === field.field.fieldNameAlias &&
      filterDetail.querySourceFieldId === field.fieldId &&
      filterDetail.sourceDataObjectName === constants.FIELD_TYPE.AggregatedField
    );
  }

  /**
   * Remove AggregatedField
   * @param {Object} field
   */
  @action
  removeAggregatedField(field: any) {
    // Find filter use deleted aggregated field
    this.filterFields = map(filterDetail => {
      if (this.isFilterUseAggregatedField(field, filterDetail)) {
        let details = clone(filterDetail);
        details.state = constants.OBJECT_STATE.DELETE;
        details.alias = '';
        details.cascading = !filterDetail.isParameter;
        return new FilterDetails(details, this.filterFields);
      }
      return filterDetail;
    }, this.filterFields);
  }

  /**
   * Update report part chart title of filter detail which using aggregated field
   * @param {string} oldTitle
   * @param {string} newTitle
   */
  @action
  updateAggregatedFieldReportPartTitle(oldTitle: string, newTitle: string) {
    forEach(filterDetail => {
      if (filterDetail.reportPartTitle === oldTitle) {
        filterDetail.reportPartTitle = newTitle;
      }
    }, this.filterFields);
  }

  /**
   * Update report field name alias filter detail which using aggregated field
   * @param {string} oldAlias
   * @param {string} newAlias
   * @param {string} reportPartTitle
   */
  @action
  updateAggregatedFieldReportFieldAlias(oldAlias: string, newAlias: string, reportPartTitle: string) {
    forEach(filterDetail => {
      if (filterDetail.reportPartTitle === reportPartTitle && filterDetail.reportFieldAlias === oldAlias) {
        filterDetail.sourceFieldName = newAlias;
        filterDetail.reportFieldAlias = newAlias;
      }
    }, this.filterFields);
  }

  /**
   * Find normal filter marked as "Required" with empty value
   * @param {Object} field
   */
  isEmptyRequiredFilter(field: any): boolean {
    const isEmptyValue = isEmpty(trim(field.value)) || includes('', split(';#', field.value));
    return !field.isParameter && field.required && isEmptyValue && this.needCheckOperatorValue(field);
  }

  /**
   * Which operator is need to check it's value
   * @param {Object} field
   */
  needCheckOperatorValue(field: any) {
    const operatorName = field.operatorName;
    return operatorName && !includes(operatorName, OPERATOR_NAMES);
  }

  /**
   * Remove filters which related which uncheck DataObject
   * @param {Object} dataObject
   */
  removeFiltersOfUncheckedDataObject(dataObject: any) {
    for (let index = 0; index < this.filterFields.length; index++) {
      const item = this.filterFields[index];
      if (item.querySourceId === dataObject.id && item.state !== ObjectStateConstants.DELETE) {
        if (!item.id) {
          this.filterFields.splice(index, 1);
          index--;
        } else {
          item.state = ObjectStateConstants.DELETE;
        }
      }
    }
    this.reorderPosition();
  }

  /**
   * Check if report filter use this field
   * @param {Object} field
   */
  isFilterUseCalculatedField(field: any) {
    return !!find(filterDetail => {
      return (
        filterDetail.state != ObjectStateConstants.DELETE &&
        filterDetail.querySourceType === constants.CALCULATED_FIELD_NAME &&
        filterDetail.sourceFieldName === field.name
      );
    }, this.filterFields);
  }

  /**
   * Set Dirty
   */
  markUpdateResult() {
    this.needUpdateResult = true;
  }

  /**
   * Reset Update result
   */
  resetUpdateResult() {
    this.needUpdateResult = false;
  }

  /**
   * Update FilterInfo For SubReport
   */
  updateFilterInfoForSubReport() {
    const format = FILTER_URL.OVERIDE_MASTER_REPORT_FILTER.FORMAT;
    const useUserLookUp = FILTER_URL.OVERIDE_MASTER_REPORT_USE_USER_LOOKUP.FORMAT;

    this.filterInfoForSubReport = reduce(
      function(acc, filterDetail) {
        let value = set(String.format(format, filterDetail.position), filterDetail.value, acc);
        return set(String.format(useUserLookUp, filterDetail.position), filterDetail.useLookup, value);
      },
      {},
      this.filterFields
    );
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerFilter.js